import React, { useState, useContext } from 'react';
import MDBox from 'src/components/md/MDBox';
import ErrorMessage from 'src/components/errorMessage/ErrorMessage';
import { CircularProgress, IconButton } from '@mui/material';
import MDButton from 'src/components/md/MDButton';
import MDTypography from 'src/components/md/MDTypography';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DataTable from 'src/md-examples/Tables/DataTable';
import { Column } from 'react-table';
import useCoreEnums, {
  CoreEnumDTO,
} from 'src/api/core/core-enums/useCoreEnums';
import useDeleteCoreEnum from 'src/api/core/core-enums/useDeleteCoreEnum';
import { EnumDetailPage } from 'src/pages/authenticated/settings/organization/components/core-enums/EnumDetailPage';
import ConfirmDialog from 'src/components/common/ConfirmDialog';
import { AuthContext } from 'src/context/auth.context';
import { useViewMode } from 'src/context/view-mode.context';
import { isAdmin as checkIsAdmin } from 'src/utils/user';

const CoreEnumsCard: React.FC = () => {
  const { currentUser } = useContext(AuthContext);
  const { viewMode } = useViewMode();
  const isAdmin = checkIsAdmin(currentUser, viewMode);

  const coreEnums = useCoreEnums({ includeOptions: true });
  const [showEditPage, setShowEditPage] = useState(false);
  const [enumToEdit, setEnumToEdit] = useState<CoreEnumDTO | null>(null);
  const [enumToDelete, setEnumToDelete] = useState<CoreEnumDTO | null>(null);

  const { mutate: deleteEnum } = useDeleteCoreEnum(enumToDelete?.id || '', () =>
    setEnumToDelete(null),
  );

  const loading = coreEnums.isLoading;
  const error = coreEnums.error;

  const handleOpenCreate = () => {
    setEnumToEdit(null);
    setShowEditPage(true);
  };

  const handleOpenEdit = (enumData: CoreEnumDTO) => {
    setEnumToEdit(enumData);
    setShowEditPage(true);
  };

  const handleDelete = (enumData: CoreEnumDTO) => {
    setEnumToDelete(enumData);
  };

  const confirmDelete = () => {
    if (enumToDelete) {
      deleteEnum();
    }
  };

  // Show edit page if editing
  if (showEditPage) {
    return (
      <EnumDetailPage
        enumToEdit={enumToEdit}
        onClose={() => {
          setShowEditPage(false);
          setEnumToEdit(null);
        }}
      />
    );
  }

  const columns: Column<CoreEnumDTO>[] = [
    {
      Header: 'Name',
      accessor: 'name',
      Cell: ({ row }: { row: { original: CoreEnumDTO } }) => (
        <MDBox display="flex" alignItems="center" gap={1}>
          {row.original.isSystem && (
            <MDBox
              component="span"
              sx={{
                display: 'flex',
                alignItems: 'center',
                color: 'info.main',
              }}
              title="System-managed enum"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
            </MDBox>
          )}
          {row.original.areOptionsLocked && (
            <MDBox
              component="span"
              sx={{
                display: 'flex',
                alignItems: 'center',
                color: '#9333ea',
              }}
              title="Locked enum options"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
            </MDBox>
          )}
          <MDTypography fontSize="inherit" fontWeight="inherit">
            {row.original.name}
          </MDTypography>
          {row.original.isSystem && (
            <MDBox
              component="span"
              sx={{
                backgroundColor: 'info.main',
                color: 'white',
                px: 1,
                py: 0.25,
                borderRadius: 1,
                fontSize: '0.75rem',
                fontWeight: 600,
              }}
            >
              SYSTEM
            </MDBox>
          )}
          {row.original.areOptionsLocked && (
            <MDBox
              component="span"
              sx={{
                backgroundColor: '#9333ea',
                color: 'white',
                px: 1,
                py: 0.25,
                borderRadius: 1,
                fontSize: '0.75rem',
                fontWeight: 600,
              }}
            >
              LOCKED
            </MDBox>
          )}
        </MDBox>
      ),
    },
    {
      Header: 'Description',
      accessor: 'description',
      Cell: ({ value }: { value: string }) => (
        <MDTypography
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '300px',
            fontSize: 'inherit',
            fontWeight: 'inherit',
            color: 'inherit',
          }}
        >
          {value || '-'}
        </MDTypography>
      ),
    },
    {
      Header: 'Options',
      id: 'options',
      accessor: (row) => row.options?.length || 0,
      Cell: ({ row }: { row: { original: CoreEnumDTO } }) => {
        const optionCount = row.original.optionCount || 0;
        return (
          <MDBox
            component="span"
            sx={{
              backgroundColor: 'grey.200',
              color: 'grey.700',
              px: 1.5,
              py: 0.5,
              borderRadius: 4,
              fontSize: '0.875rem',
              fontWeight: 500,
              display: 'inline-block',
            }}
          >
            {optionCount} {optionCount === 1 ? 'option' : 'options'}
          </MDBox>
        );
      },
    },
    {
      Header: '',
      id: 'actions',
      Cell: ({ row }: { row: { original: CoreEnumDTO } }) => {
        const enumData = row.original;
        const canEdit =
          isAdmin || !(enumData.isSystem && enumData.areOptionsLocked);
        return (
          <MDBox display="flex" textAlign={'right'}>
            {canEdit && (
              <IconButton
                onClick={() => handleOpenEdit(enumData)}
                aria-label="edit"
                size="small"
                sx={{
                  opacity: 0.7,
                  color: 'grey.600',
                  '&:hover': { opacity: 1 },
                }}
                title={enumData.isSystem ? 'Edit options only' : 'Edit enum'}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            )}
            {!enumData.isSystem && (
              <IconButton
                onClick={() => handleDelete(enumData)}
                aria-label="delete"
                size="small"
                sx={{
                  opacity: 0.7,
                  color: 'grey.600',
                  '&:hover': { opacity: 1 },
                }}
                title="Delete enum"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          </MDBox>
        );
      },
    },
  ];

  if (loading) {
    return (
      <MDBox pt={2} pb={3} px={3} display="flex" justifyContent="center">
        <CircularProgress />
      </MDBox>
    );
  } else if (error) {
    return <ErrorMessage error={error} />;
  }

  return (
    <MDBox pt={2} pb={3} px={3}>
      {/* Header with Create button (Admin only) */}
      {isAdmin && (
        <MDBox display="flex" justifyContent="flex-end" mb={2}>
          <MDButton
            variant="contained"
            color="primary"
            onClick={handleOpenCreate}
          >
            Create Enum
          </MDButton>
        </MDBox>
      )}

      {coreEnums.data?.edges.length === 0 ? (
        <MDBox
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          textAlign="center"
          py={4}
        >
          <MDBox mb={2}>
            <MDTypography variant="body2" color="text" sx={{ mt: 0, mb: 4 }}>
              {isAdmin
                ? `It looks like there aren't any core enums created yet. Click "Create Enum" to get started.`
                : `It looks like there aren't any core enums created yet.`}
            </MDTypography>
          </MDBox>
        </MDBox>
      ) : (
        <DataTable<CoreEnumDTO>
          canSearch={false}
          table={{ columns, rows: coreEnums.data?.edges ?? [] }}
          pagination={{ variant: 'gradient', color: 'info' }}
          showTotalEntries={false}
          isSorted={false}
        />
      )}

      <ConfirmDialog
        open={!!enumToDelete}
        onClose={() => setEnumToDelete(null)}
        onConfirm={confirmDelete}
        title="Delete Enum"
        message={`Are you sure you want to delete "${enumToDelete?.name}"? This action cannot be undone.`}
      />
    </MDBox>
  );
};

export default CoreEnumsCard;
